import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, UserCog, Bot, Coins, Home, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import IsnIntegrationCard from "@/components/settings/IsnIntegrationCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  // Limited access (never activated / subscription ended): profile only — ISN calls would 402.
  const limited = !isAdmin && !!user?.billing?.enabled && !!user?.billing?.blocked;
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Account</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
          Settings
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {limited ? "Your account is in limited access — you can update your profile and password here." : "Manage your profile, password, and scraping preferences."}
        </p>
      </div>
      <ProfileCard />
      {!limited ? <IsnIntegrationCard /> : null}
      {isAdmin ? <FiscalYearCard /> : null}
      {isAdmin ? <ScrapingCard /> : null}
    </div>
  );
}

function ProfileCard() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [currency, setCurrency] = useState("CAD");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setLocation(user.location || "");
      setHomeAddress(user.home_address || "");
      setCurrency(user.currency || "CAD");
    }
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { name, location, home_address: homeAddress, currency };
      if (password.trim()) body.password = password.trim();
      await api.put("/me/profile", body);
      setPassword("");
      await refreshMe();
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-5"
      data-testid="profile-section"
    >
      <div className="flex items-start gap-3 max-w-xl">
        <div className="rounded-md bg-slate-100 p-2 shrink-0">
          <UserCog className="h-4 w-4 text-slate-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900 font-display">
            Profile
          </h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Your location is used to scope all brokerage discovery and social
            enrichment searches to your area.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
          <Input
            value={user?.email || ""}
            disabled
            readOnly
            className="mt-1 min-h-[44px] bg-slate-50"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 min-h-[44px]"
            data-testid="profile-name-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Location (used for scraping)
          </Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Saskatoon, SK"
            className="mt-1 min-h-[44px]"
            data-testid="profile-location-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Free-text. We use the first portion (before the comma) as the city hint.
          </p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <Home className="h-3 w-3" /> Home / office address (start point for open-house routes)
          </Label>
          <Input
            value={homeAddress}
            onChange={(e) => setHomeAddress(e.target.value)}
            placeholder="e.g. 410 22nd St E, Saskatoon, SK S7K 5T6"
            className="mt-1 min-h-[44px]"
            data-testid="profile-home-address-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Used as the route origin when GPS isn't available. The browser will be asked for
            location permission first; this is the fallback. You can always override on the
            route screen.
          </p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <Coins className="h-3 w-3" /> Currency
          </Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger
              className="mt-1 min-h-[44px] sm:w-56"
              data-testid="profile-currency-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CAD">CAD — Canadian dollar</SelectItem>
              <SelectItem value="USD">USD — US dollar</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">
            Sets the currency shown on referral/revenue amounts. Defaults from
            your location; change it here anytime. (Display only — no conversion.)
          </p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            New password{" "}
            <span className="text-slate-400 normal-case">(leave blank to keep current)</span>
          </Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter new password…"
            className="mt-1 min-h-[44px]"
            data-testid="profile-password-input"
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          onClick={save}
          disabled={busy}
          className="text-white min-h-[44px]"
          style={{ background: "hsl(var(--brand))" }}
          data-testid="save-profile-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save profile
        </Button>
      </div>
    </section>
  );
}

function FiscalYearCard() {
  const [month, setMonth] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/system/fiscal-year");
        setMonth(String(data.fiscal_year_start_month));
      } catch {
        setMonth("1");
      }
    })();
  }, []);

  const save = async (val) => {
    setMonth(val);
    setBusy(true);
    try {
      await api.put("/system/fiscal-year", { fiscal_year_start_month: Number(val) });
      toast.success("Fiscal year updated");
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  if (month === null) return null;

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-5"
      data-testid="fiscal-year-section"
    >
      <div className="flex items-start gap-3 max-w-xl">
        <div className="rounded-md bg-slate-100 p-2 shrink-0">
          <Calendar className="h-4 w-4 text-slate-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900 font-display">
            Fiscal year
          </h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Sets the start month for "This Year" on the Referral partner ROI widget.
            A fiscal year starting in October runs Oct 1 → Sep 30.
          </p>
        </div>
      </div>
      <div className="mt-4 max-w-xs">
        <Label className="text-xs uppercase tracking-wide text-slate-500">
          Fiscal year starts in
        </Label>
        <Select value={month} onValueChange={save} disabled={busy}>
          <SelectTrigger
            className="mt-1 min-h-[44px]"
            data-testid="fiscal-year-month-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m, i) => (
              <SelectItem key={i + 1} value={String(i + 1)}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}

function ScrapingCard() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/system/scraping-settings");
      setSettings(data);
    } catch (e) {
      toast.error("Failed to load scraping settings", { description: formatApiError(e) });
    }
  };
  useEffect(() => {
    load();
  }, []);

  const changeEngine = async (engine) => {
    setBusy(true);
    try {
      const { data } = await api.put("/system/scraping-settings", { scraping_engine: engine });
      setSettings(data);
      toast.success(`Engine set to ${LABEL[engine] || engine}`);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return (
      <section
        className="rounded-lg border border-slate-200 bg-white p-5"
        data-testid="scraping-section"
      >
        <div className="text-sm text-slate-500">Loading scraping settings…</div>
      </section>
    );
  }

  const cap = settings.firecrawl_credit_cap;
  const used = settings.firecrawl_credits_used;
  const remaining = settings.firecrawl_credits_remaining;
  const pct = Math.min(100, Math.round((settings.firecrawl_credit_pct || 0) * 100));
  const overBudget = !!settings.firecrawl_disabled_reason;
  const ready = settings.firecrawl_configured && !overBudget;

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-5"
      data-testid="scraping-section"
    >
      <div className="flex items-start gap-3 max-w-xl">
        <div className="rounded-md bg-slate-100 p-2 shrink-0">
          <Bot className="h-4 w-4 text-slate-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900 font-display">
            Scraping engine
          </h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Firecrawl is the primary engine — it handles multi-page agent
            rosters automatically. Playwright stays available as a fallback
            for sites Firecrawl can't reach or when you've used your monthly
            credit allotment.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Engine preference
          </Label>
          <Select
            value={settings.scraping_engine}
            onValueChange={changeEngine}
            disabled={busy}
          >
            <SelectTrigger
              className="mt-1 min-h-[44px]"
              data-testid="scraping-engine-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (Apify → Firecrawl → Playwright)</SelectItem>
              <SelectItem value="apify">Apify only (Realtor.ca)</SelectItem>
              <SelectItem value="firecrawl">Firecrawl only</SelectItem>
              <SelectItem value="playwright">Playwright only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Status
          </Label>
          <div className="mt-1 flex items-center h-[44px]">
            {settings.firecrawl_configured ? (
              ready ? (
                <span
                  className="px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                  data-testid="firecrawl-status-badge"
                >
                  Firecrawl ready
                </span>
              ) : (
                <span
                  className="px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200 text-xs"
                  data-testid="firecrawl-status-badge"
                >
                  Auto-paused (credit cap)
                </span>
              )
            ) : (
              <span
                className="px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 text-xs"
                data-testid="firecrawl-status-badge"
              >
                Not configured
              </span>
            )}
          </div>
        </div>
      </div>

      {settings.firecrawl_configured ? (
        <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span className="flex items-center gap-1">
              <Coins className="h-3.5 w-3.5" /> Firecrawl credits ·{" "}
              <span className="text-slate-500">{settings.firecrawl_month}</span>
            </span>
            <span
              className="text-slate-700 font-medium"
              data-testid="firecrawl-credits-text"
            >
              {used.toLocaleString()} / {cap.toLocaleString()} used
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={
                pct >= 80
                  ? "h-full bg-amber-500"
                  : "h-full bg-emerald-500"
              }
              style={{ width: `${pct}%` }}
              data-testid="firecrawl-credits-bar"
            />
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {remaining.toLocaleString()} credits remaining this month
            {overBudget ? (
              <span className="text-amber-700"> · auto-fallback to Playwright until next month</span>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-500 mt-3">
          Set <code className="bg-slate-100 px-1 rounded">FIRECRAWL_API_KEY</code> in the backend
          environment to enable Firecrawl. Without it, only the Playwright engine runs.
        </p>
      )}

      {/* Apify monthly credit usage — refreshed every 5 min via /api/system/apify-usage */}
      <ApifyUsageBanner />
    </section>
  );
}

function ApifyUsageBanner() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/system/apify-usage");
        if (alive) {
          setData(data);
          setErr("");
        }
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  if (err) {
    return (
      <div
        className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
        data-testid="apify-usage-banner"
      >
        Apify usage unavailable: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-400"
        data-testid="apify-usage-banner"
      >
        Loading Apify credits…
      </div>
    );
  }
  if (!data.configured) {
    return (
      <div
        className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
        data-testid="apify-usage-banner"
      >
        Apify not configured. Set{" "}
        <code className="bg-slate-100 px-1 rounded">APIFY_API_TOKEN</code> in the backend
        environment to enable Apify-driven scans.
      </div>
    );
  }

  const usagePct = Math.min(100, Math.round((data.pct || 0) * 100));
  const isOver = data.monthly_usage_usd >= data.monthly_cap_usd;
  const isWarning = !isOver && usagePct >= 80;
  let badgeCls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  let badgeText = "Apify ready";
  let barCls = "bg-emerald-500";
  if (isOver) {
    badgeCls = "bg-red-50 text-red-700 border-red-200";
    badgeText = "Apify at cap";
    barCls = "bg-red-500";
  } else if (isWarning) {
    badgeCls = "bg-amber-50 text-amber-700 border-amber-200";
    badgeText = "Apify nearing cap";
    barCls = "bg-amber-500";
  }

  // Cycle range for the subtitle (e.g. "Jun 8 → Jul 7")
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };
  const cycleLabel =
    data.cycle_start && data.cycle_end
      ? `${fmt(data.cycle_start)} → ${fmt(data.cycle_end)}`
      : "this month";

  return (
    <div
      className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3"
      data-testid="apify-usage-banner"
    >
      <div className="flex items-center justify-between text-xs text-slate-600 gap-2 flex-wrap">
        <span className="flex items-center gap-1">
          <Coins className="h-3.5 w-3.5" /> Apify credits ·{" "}
          <span className="text-slate-500">{cycleLabel}</span>
        </span>
        <span
          className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide ${badgeCls}`}
          data-testid="apify-status-badge"
        >
          {badgeText}
        </span>
      </div>
      <div className="flex items-center justify-between text-xs text-slate-700 mt-1.5">
        <span className="tabular-nums" data-testid="apify-credits-text">
          ${data.monthly_usage_usd.toFixed(2)} used / ${data.monthly_cap_usd.toFixed(2)} monthly credit
        </span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full ${barCls}`}
          style={{ width: `${usagePct}%` }}
          data-testid="apify-credits-bar"
        />
      </div>
      <div className="text-xs text-slate-500 mt-2">
        ${data.remaining_usd.toFixed(2)} remaining · resets monthly
      </div>
    </div>
  );
}

const LABEL = {
  auto: "Auto (Apify → Firecrawl → Playwright)",
  apify: "Apify only (Realtor.ca)",
  firecrawl: "Firecrawl only",
  playwright: "Playwright only",
};
